/* Inline collection filter bar — open/close behaviour only.
 *
 * Everything to do with submitting, fetching and re-rendering stays in
 * facets.js: the bar renders inside the same #FacetFiltersForm, so ticking a
 * box already triggers the theme's debounced submit. This file adds the chip
 * expand/collapse, and translates the price bands into the two hidden
 * filter.v.price inputs the form carries.
 *
 * Open state lives as a class on .js-filter, never on the chip or the panel.
 * facets.js re-renders a group by replacing its innerHTML, so any state held
 * on an element inside it is destroyed on every filter change — the wrapper is
 * the only thing that survives. aria-expanded is re-synced from that class by
 * the observer at the foot of this file.
 */
(function () {
  "use strict";

  var OPEN = "filter-bar__group--open";
  // Below 990px the chips are hidden and one Filter button slides the whole
  // set out as a drawer. Class on the bar root, because the bar root is the
  // one element in here facets.js never re-renders.
  var DRAWER = "filter-bar--drawer-open";

  function groups() {
    return document.querySelectorAll(".filter-bar__group");
  }

  function bar() {
    return document.querySelector(".filter-bar");
  }

  function closeAll(except) {
    groups().forEach(function (group) {
      if (group !== except) group.classList.remove(OPEN);
    });
    sync();
  }

  function sync() {
    groups().forEach(function (group) {
      var chip = group.querySelector("[data-filter-chip]");
      if (!chip) return;
      var open = group.classList.contains(OPEN) ? "true" : "false";
      // Guard the write: the observer below watches for exactly this kind of
      // mutation, and setting an unchanged value would loop.
      if (chip.getAttribute("aria-expanded") !== open) {
        chip.setAttribute("aria-expanded", open);
      }
    });
    syncToggle();
  }

  // The Filter button's own state: pressed-ness, and the badge.
  //
  // The badge is summed from the chips rather than printed by Liquid. Each
  // chip already renders its own count and facets.js re-renders the chips on
  // every filter change, so the sum is right by construction -- a number
  // written once at page load would not be.
  function syncToggle() {
    var root = bar();
    var toggle = document.querySelector("[data-filter-toggle]");
    if (!root || !toggle) return;

    var open = root.classList.contains(DRAWER) ? "true" : "false";
    if (toggle.getAttribute("aria-expanded") !== open) {
      toggle.setAttribute("aria-expanded", open);
    }

    // Scoped to the panels container, not the document. The badge on this
    // button carries .filter-bar__chip-count too -- it is styled by the same
    // rule as the chips' own markers -- so an unscoped query counted the
    // badge into its own total and it climbed on every sync: 2, 8, 14.
    var total = 0;
    var panels = drawerPanel();
    if (panels) {
      panels.querySelectorAll(".filter-bar__chip-count").forEach(function (node) {
        var n = parseInt(node.textContent, 10);
        if (!isNaN(n)) total += n;
      });
    }

    var badge = toggle.querySelector("[data-filter-toggle-count]");
    if (!badge) return;
    if (total > 0) {
      if (badge.textContent !== String(total)) badge.textContent = total;
      badge.hidden = false;
    } else {
      badge.textContent = "";
      badge.hidden = true;
    }
  }

  function anyOpen() {
    var root = bar();
    return (
      !!document.querySelector("." + OPEN) ||
      !!(root && root.classList.contains(DRAWER))
    );
  }

  function drawerPanel() {
    return document.getElementById("FilterBarPanels");
  }

  function openDrawer() {
    var root = bar();
    var panel = drawerPanel();
    if (!root || !panel) return;
    // The two modes are exclusive. A single group left open from a wider
    // viewport would otherwise still be expanded underneath.
    closeAll(null);
    root.classList.add(DRAWER);
    // Only announced as a dialog while it is one. Above 990px this element is
    // display: contents -- part of the chip row, not a window over the page.
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-modal", "true");
    document.body.classList.add("overflow-hidden");
    syncToggle();
    // The theme's own helper, the same one the cart and menu drawers use. It
    // keeps Tab inside the panel and restores focus through removeTrapFocus.
    if (typeof trapFocus === "function") {
      trapFocus(panel, panel.querySelector("[data-filter-close]") || panel);
    }
  }

  function closeDrawer() {
    var root = bar();
    var panel = drawerPanel();
    if (!root || !root.classList.contains(DRAWER)) return;
    root.classList.remove(DRAWER);
    if (panel) {
      panel.removeAttribute("role");
      panel.removeAttribute("aria-modal");
    }
    document.body.classList.remove("overflow-hidden");
    var toggle = document.querySelector("[data-filter-toggle]");
    if (typeof removeTrapFocus === "function") {
      removeTrapFocus(toggle);
    } else if (toggle) {
      toggle.focus();
    }
    syncToggle();
  }

  function closeAllPanels() {
    closeDrawer();
    closeAll(null);
  }

  document.addEventListener("click", function (event) {
    // Not every click target is an element. A click dispatched at the
    // document has no closest(), and the unguarded call below threw before
    // reaching the chip toggle -- which would have taken the whole handler
    // down with it.
    if (!event.target || !event.target.closest) return;

    var toggle = event.target.closest("[data-filter-toggle]");
    if (toggle) {
      event.preventDefault();
      var root = bar();
      if (root.classList.contains(DRAWER)) {
        closeDrawer();
      } else {
        openDrawer();
      }
      return;
    }

    // The X in the drawer head and the Show results button in its foot.
    if (event.target.closest("[data-filter-close]")) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    // The scrim is inside .filter-bar, so the outside-click rule at the foot
    // of this handler never sees it.
    if (event.target.closest("[data-filter-scrim]")) {
      closeDrawer();
      return;
    }

    var chip = event.target.closest("[data-filter-chip]");
    if (chip) {
      event.preventDefault();
      var group = chip.closest(".filter-bar__group");
      var wasOpen = group.classList.contains(OPEN);
      closeAll(group);
      group.classList.toggle(OPEN, !wasOpen);
      sync();
      armScrollDismiss(group.classList.contains(OPEN));
      return;
    }

    var band = event.target.closest("[data-price-band]");
    if (band) {
      event.preventDefault();
      applyBand(band);
      return;
    }

    // A click anywhere outside the bar closes the open panel. Clicks inside
    // it must not: ticking a filter is meant to leave the panel up so a
    // shopper can tick a second one.
    if (!event.target.closest(".filter-bar")) closeAllPanels();
  });

  // Touch fallback for the tap-to-dismiss above.
  //
  // iOS Safari only dispatches a click to the document for taps that land on
  // something interactive. A tap on dead space -- the page background, the
  // margin beside the grid, the gap between two cards -- produces touch
  // events and no click at all, so the handler above never ran and the panel
  // stayed open on the devices where it matters most.
  //
  // Deliberately scoped to taps that would not have produced a click anyway.
  // Closing the panel removes about 130px from the sticky bar and lifts
  // everything below it, so doing that between touchend and the click of a
  // tap on a product card would move the card out from under the finger.
  // Links and controls are left to the click handler for that reason.
  var tapOrigin = null;

  document.addEventListener(
    "touchstart",
    function (event) {
      var touch = event.changedTouches[0];
      tapOrigin = touch ? { x: touch.clientX, y: touch.clientY } : null;
    },
    { passive: true }
  );

  document.addEventListener(
    "touchend",
    function (event) {
      var origin = tapOrigin;
      tapOrigin = null;
      if (!origin || !anyOpen()) return;

      // A finger that travelled was scrolling, not dismissing.
      var touch = event.changedTouches[0];
      if (
        !touch ||
        Math.abs(touch.clientX - origin.x) > 10 ||
        Math.abs(touch.clientY - origin.y) > 10
      ) {
        return;
      }

      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest(".filter-bar")) return;
      // tabindex="-1" is excluded on purpose: it means "focusable from
      // script", not "interactive". The theme puts it on <main>, so a bare
      // [tabindex] here matched every element on the page and this handler
      // never fired at all.
      if (
        target.closest(
          'a, button, input, select, textarea, summary, label,' +
            ' [role="button"], [tabindex]:not([tabindex="-1"])'
        )
      ) {
        return;
      }

      closeAllPanels();
    },
    { passive: true }
  );

  // Scrolling away dismisses the panel on phones. Open, it rides along inside
  // the sticky bar and takes roughly a fifth of the screen with it. Left alone
  // from 750px up, where there is room to spare and a shopper may well want to
  // nudge the page while comparing bands against the results.
  var narrow = window.matchMedia("(max-width: 749.99px)");
  var scrollAnchor = null; // null means not armed yet
  var settleTimer = null;

  function settle() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      scrollAnchor = window.scrollY;
    }, 250);
  }

  function armScrollDismiss(isOpen) {
    clearTimeout(settleTimer);
    scrollAnchor = null;
    // The baseline is taken once the page has been still for a moment, not
    // at the instant the chip is tapped. On iOS a flick keeps gliding after
    // the finger leaves, so a shopper can open a panel mid-scroll -- and the
    // tail of their own scroll would then dismiss what they just opened.
    if (isOpen) settle();
  }

  window.addEventListener(
    "scroll",
    function () {
      if (!narrow.matches) return;
      if (!anyOpen()) return;
      // Never while the drawer is open. It locks the body anyway, so the page
      // behind it cannot move -- and a scroll inside the drawer must not be
      // read as a request to close it.
      if (document.querySelector("." + DRAWER)) return;

      // Still moving: push the baseline out until it stops.
      if (scrollAnchor === null) {
        settle();
        return;
      }

      // A threshold rather than any movement at all. Opening the panel grows
      // the sticky bar and applying a filter re-renders the grid; either can
      // shift the scroll position by a pixel or two without the shopper
      // having moved a finger, and the panel is meant to stay up long enough
      // to tick a second box.
      if (Math.abs(window.scrollY - scrollAnchor) < 24) return;
      closeAllPanels();
      armScrollDismiss(false);
    },
    { passive: true }
  );

  document.addEventListener("keydown", function (event) {
    if (event.key !== "Escape") return;
    if (!anyOpen()) return;

    // The drawer restores focus itself, through the theme's removeTrapFocus.
    if (bar() && bar().classList.contains(DRAWER)) {
      closeDrawer();
      return;
    }

    var open = document.querySelector("." + OPEN);
    var returnTo = open ? open.querySelector("[data-filter-chip]") : null;
    closeAll(null);
    returnTo?.focus();
  });

  // Crossing back above the breakpoint mid-drawer would leave the body locked
  // and a dialog role on an element that is display: contents again.
  var wide = window.matchMedia("(min-width: 990px)");
  var onWide = function (event) {
    if (event.matches) closeDrawer();
  };
  if (wide.addEventListener) {
    wide.addEventListener("change", onWide);
  } else if (wide.addListener) {
    wide.addListener(onWide);
  }

  function applyBand(band) {
    var panel = band.closest("[data-filter-panel]");
    var form = band.closest("form");
    if (!panel || !form) return;

    var gte = panel.querySelector('[data-price-input="gte"]');
    var lte = panel.querySelector('[data-price-input="lte"]');
    if (!gte || !lte) return;

    // Bands are exclusive, and clicking the selected one clears it — the same
    // affordance as tapping an active chip, and the only way to get back to
    // "any price" without reaching for Clear all.
    var selected = band.classList.contains("filter-bar__option--selected");

    panel.querySelectorAll("[data-price-band]").forEach(function (other) {
      other.classList.remove("filter-bar__option--selected");
      other.setAttribute("aria-pressed", "false");
    });

    if (selected) {
      gte.value = "";
      lte.value = "";
    } else {
      band.classList.add("filter-bar__option--selected");
      band.setAttribute("aria-pressed", "true");
      gte.value = band.dataset.gte || "";
      lte.value = band.dataset.lte || "";
    }

    // Hand back to facets.js. Its listener sits on the form itself, so the
    // event has to bubble up from the input to reach it.
    form.dispatchEvent(new Event("input", { bubbles: true }));
  }

  function observe() {
    var container = document.querySelector(".filter-bar__groups");
    if (!container) return;
    new MutationObserver(sync).observe(container, {
      childList: true,
      subtree: true,
    });
    sync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observe);
  } else {
    observe();
  }
})();
